// App Header - Duolingo-style gamification display
// Shows streak and Knowledge Points prominently (always visible)
// Custom driving theme (not Duolingo colors/emojis)

import React, { useState, useEffect, useCallback } from 'react';

import { gamificationService } from '../services/gamificationService';
import { userAuth } from '../services/userAuth';
import { subscriptionService } from '../services/subscriptionService';
import { useToastContext } from '../contexts/ToastContext';
import { SettingsDropdown } from './SettingsDropdown';
import { useLanguage } from '../contexts/LanguageContext';
import './AppHeader.css';
import { useNavigate } from 'react-router-dom';
import { Zap } from 'lucide-react';

export const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const [_gamificationData, setGamificationData] = useState(gamificationService.getData());
  const [dueReviewsCount, setDueReviewsCount] = useState(0);
  const [hasSubscription, setHasSubscription] = useState(subscriptionService.hasActiveSubscription());
  const { t_nested } = useLanguage();
  const toast = useToastContext();
  // Helper to get translation with proper fallback
  const getTranslated = useCallback((key: string, fallback: string): string => {
    const translated = t_nested(key);
    // If translation returns the key itself, use fallback
    return translated === key ? fallback : translated;
  }, [t_nested]);

  // Check if user is guest
  const currentUser = userAuth.getCurrentUser();
  const isGuest = !currentUser || currentUser.isGuest;

  useEffect(() => {
    // Initialize gamification
    gamificationService.initialize();
    setGamificationData(gamificationService.getData());
    setHasSubscription(subscriptionService.hasActiveSubscription());

    // Check for due reviews
    const checkDueReviews = async () => {
      try {
        const { spacedRepetitionService } = await import('../services/spacedRepetitionService');
        const dueReviews = await spacedRepetitionService.getDueReviews();
        setDueReviewsCount(dueReviews.length);
      } catch (error) {
        // Silently fail if service not available
      }
    };
    checkDueReviews();

    // Update every 5 seconds
    const interval = setInterval(() => {
      setGamificationData(gamificationService.getData());
      setHasSubscription(subscriptionService.hasActiveSubscription());
      checkDueReviews();
    }, 5000);

    return () => clearInterval(interval);
  }, [toast, getTranslated]);

  return (
    <>
      <header className="app-header">
        <div className="header-content">
          {/* Gamification Display */}
          <div className="header-gamification">
            {/* Review Badge - Show if reviews are due */}
            {!isGuest && dueReviewsCount > 0 && (
              <button
                className="header-review-badge"
                title={`${dueReviewsCount} topic${dueReviewsCount > 1 ? 's' : ''} need${dueReviewsCount > 1 ? '' : 's'} review`}
                aria-label={`${dueReviewsCount} reviews due`}
                onClick={async () => {
                  // Navigate to dashboard or show reviews
                  navigate('/');
                }}
              >
                <span className="review-badge-icon">📚</span>
                <span className="review-badge-count">{dueReviewsCount}</span>
              </button>
            )}

            {/* Upgrade Pill - Show if authenticated and not subscribed */}
            {!isGuest && !hasSubscription && (
              <button
                className="header-upgrade-pill"
                onClick={() => navigate('/subscription')}
                aria-label={getTranslated('header.upgradeToPro', 'Upgrade')}
                title={getTranslated('header.upgradeToPro', 'Upgrade')}
              >
                <Zap size={16} className="upgrade-pill-icon" />
                <span className="upgrade-pill-text">{getTranslated('header.upgradeToPro', 'Upgrade')}</span>
              </button>
            )}
          </div>

{/* Share and Settings - Top Right (Web and Mobile) */}
          <div className="header-settings-container">
            <SettingsDropdown />
          </div>
        </div>
      </header>
    </>
  );
};
